import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Divider,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Assignment as AssignmentIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { getStudentTrends } from '../../services/teacherService';
import { StudentTrend } from '../../types/teacher';

interface StudentTrendChartProps {
  studentId: number;
  studentName: string;
  refreshTrigger?: number;
}

const StudentTrendChart: React.FC<StudentTrendChartProps> = ({
  studentId,
  studentName,
  refreshTrigger
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trends, setTrends] = useState<StudentTrend[]>([]);
  const [timeWindow, setTimeWindow] = useState<number>(12);

  const fetchStudentTrends = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getStudentTrends(studentId, timeWindow);
      setTrends(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load student trends';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [studentId, timeWindow]);

  useEffect(() => {
    fetchStudentTrends();
  }, [refreshTrigger, fetchStudentTrends]);

  const handleTimeWindowChange = (event: SelectChangeEvent<number>) => {
    setTimeWindow(Number(event.target.value));
  };

  const getTrendIcon = (trend: 'improving' | 'stable' | 'declining') => {
    switch (trend) {
      case 'improving':
        return <TrendingUpIcon color="success" fontSize="small" />;
      case 'declining':
        return <TrendingDownIcon color="error" fontSize="small" />;
      case 'stable':
      default:
        return <TrendingFlatIcon color="action" fontSize="small" />;
    }
  };

  const getTrendColor = (trend: 'improving' | 'stable' | 'declining') => {
    switch (trend) {
      case 'improving':
        return 'success';
      case 'declining':
        return 'error';
      case 'stable':
      default:
        return 'default';
    }
  };

  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 90) return 'success';
    if (accuracy >= 75) return 'info';
    if (accuracy >= 60) return 'warning';
    return 'error';
  };

  const calculateOverallStats = () => {
    if (trends.length === 0) return null;

    const totalSubmissions = trends.reduce((sum, t) => sum + t.submissionCount, 0);
    const avgAccuracy =
      trends.reduce((sum, t) => sum + t.accuracy, 0) / trends.length;
    const totalPoints = trends.reduce((sum, t) => sum + t.totalPoints, 0);

    // Get most recent week
    const latestTrend = trends[0];

    return {
      totalSubmissions,
      avgAccuracy: Math.round(avgAccuracy),
      totalPoints,
      latestTrend
    };
  };

  const stats = calculateOverallStats();

  return (
    <Card elevation={2}>
      <CardHeader
        avatar={<AssignmentIcon color="primary" />}
        title={`${studentName}'s Performance Trends`}
        action={
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={timeWindow}
              onChange={handleTimeWindowChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Time window' }}
            >
              <MenuItem value={4}>4 Weeks</MenuItem>
              <MenuItem value={8}>8 Weeks</MenuItem>
              <MenuItem value={12}>12 Weeks</MenuItem>
              <MenuItem value={24}>24 Weeks</MenuItem>
            </Select>
          </FormControl>
        }
      />
      <CardContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : trends.length === 0 ? (
          <Alert severity="info">No trend data available for this student yet.</Alert>
        ) : (
          <>
            {/* Overall Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center" p={2} bgcolor="background.default" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {stats?.avgAccuracy}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average Accuracy
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center" p={2} bgcolor="background.default" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {stats?.totalSubmissions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Submissions
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center" p={2} bgcolor="background.default" borderRadius={2}>
                  <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                    <TrophyIcon color="primary" />
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {stats?.totalPoints}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Points Earned
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Current Trend */}
            {stats?.latestTrend && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Current Trend
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  {getTrendIcon(stats.latestTrend.trend)}
                  <Chip
                    label={stats.latestTrend.trend.toUpperCase()}
                    color={getTrendColor(stats.latestTrend.trend) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                    size="small"
                  />
                  {stats.latestTrend.percentChange !== 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {stats.latestTrend.percentChange > 0 ? '+' : ''}
                      {Math.round(stats.latestTrend.percentChange)}% from previous week
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Weekly Breakdown */}
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              Weekly Breakdown (Last {timeWindow} Weeks)
            </Typography>
            <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
              {trends.map((trend, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  {/* Header Row */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="600">
                        Week {trend.weekNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(trend.weekStartDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {getTrendIcon(trend.trend)}
                      {trend.percentChange !== 0 && (
                        <Typography
                          variant="caption"
                          color={trend.percentChange > 0 ? 'success.main' : 'error.main'}
                          fontWeight="600"
                        >
                          {trend.percentChange > 0 ? '+' : ''}
                          {Math.round(trend.percentChange)}%
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Accuracy Progress Bar */}
                  <Box sx={{ mb: 1 }}>
                    <Box display="flex" justifyContent="space-between" mb={0.25}>
                      <Typography variant="caption" color="text.secondary">
                        Accuracy
                      </Typography>
                      <Typography variant="caption" fontWeight="600">
                        {trend.accuracy}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={trend.accuracy}
                      color={getAccuracyColor(trend.accuracy) as 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'inherit'}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  {/* Metrics Row */}
                  <Box display="flex" justifyContent="space-around" gap={2}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <AssignmentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" fontWeight="600">
                        {trend.submissionCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        problems
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TrophyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" fontWeight="600">
                        {trend.totalPoints}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        pts
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StudentTrendChart;
